/** Uma recarga após falha de chunk (deploy / cache) — evita loop infinito. */
export const CHUNK_RELOAD_SESSION_KEY = 'spa_chunk_reload_attempted';

const CHUNK_ERROR_PATTERNS = [
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Loading chunk',
    'ChunkLoadError',
    // Vite: CSS de componente lazy (ex.: PreviewRoute-*.css) 404 após deploy com aba antiga
    'Unable to preload CSS',
];

/**
 * Falha ao carregar JS/CSS lazy (Vite/Webpack) — típico após deploy com aba antiga aberta.
 * Inclui "Unable to preload CSS for /assets/*.css" do runtime do Vite.
 * @param {unknown} error
 */
export function isDynamicImportChunkError(error) {
    if (!error) {
        return false;
    }

    const msg = typeof error === 'object' && error !== null && typeof error.message === 'string'
        ? error.message
        : String(error);

    const normalized = msg.toLowerCase();

    return CHUNK_ERROR_PATTERNS.some((pattern) => {
        const p = pattern.toLowerCase();
        if (p === 'loading chunk') {
            return normalized.includes('loading chunk') && normalized.includes('failed');
        }
        return normalized.includes(p);
    });
}

export function clearChunkReloadAttempt() {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
    } catch {
        /* storage bloqueado */
    }
}

function showChunkLoadFailedMessage() {
    const text = 'Não foi possível carregar a página. Recarregue o aplicativo ou verifique sua conexão.';

    if (typeof window !== 'undefined' && window.Swal?.fire) {
        window.Swal.fire({
            title: 'Atualização disponível',
            text,
            icon: 'warning',
            confirmButtonText: 'Recarregar',
        }).then(() => {
            window.location.reload();
        });
        return;
    }

    if (typeof window !== 'undefined') {
        window.alert(text);
        window.location.reload();
    }
}

/**
 * Tenta recarregar a app uma vez; retorna true se tratou o erro (caller deve parar propagação).
 * @param {unknown} error
 */
export function handleDynamicImportChunkError(error) {
    if (!isDynamicImportChunkError(error)) {
        return false;
    }

    let alreadyAttempted = false;
    try {
        alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1';
    } catch {
        /* storage bloqueado */
    }

    if (!alreadyAttempted) {
        try {
            sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
        } catch {
            /* segue para reload mesmo assim */
        }
        window.location.reload();
        return true;
    }

    clearChunkReloadAttempt();
    showChunkLoadFailedMessage();
    return true;
}

const RETRY_DELAY_MS = 400;

/**
 * Envolve import dinâmico de rota com retry antes de falhar.
 * @param {() => Promise<unknown>} loader
 */
export function lazyRoute(loader) {
    return () => loader().catch((error) => {
        if (!isDynamicImportChunkError(error)) {
            throw error;
        }

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                loader().then(resolve).catch(reject);
            }, RETRY_DELAY_MS);
        });
    });
}

/**
 * Regista handler global para rejeições de import dinâmico (antes do Sentry).
 */
export function installChunkLoadUnhandledRejectionGuard() {
    if (typeof window === 'undefined') {
        return;
    }

    window.addEventListener(
        'unhandledrejection',
        (event) => {
            if (handleDynamicImportChunkError(event.reason)) {
                event.preventDefault();
            }
        },
        true
    );
}
